import saveAs from 'file-saver';

export async function retryWithMaxAttempts(
  action: any,
  maxRetries: number,
  delay = 2000,
  ...args: any
) {
  let attempt = 0;

  while (attempt < maxRetries) {
    try {
      return await action(...args);
    } catch (error) {
      attempt++;
      console.error(`Error on attempt ${attempt}:`, error);

      if (attempt === maxRetries) {
        console.error('Max retries reached. Throwing error...');
        throw error; // Re-throw the error if max retries are reached
      }

      // Optional: Delay before the next attempt
      if (delay > 0) {
        await new Promise((resolve) => setTimeout(resolve, delay * 2));
      }
    }
  }
}

export const convertPDF = async (Core: any, fileURL: any) => {
  const buf = await Core.officeToPDFBuffer(fileURL);
  // Optionally save the blob to a file or upload to a server
  const blob = new Blob([buf], { type: 'application/pdf' });
  const file = blob;
  if (file) {
    return file;
  } else {
    throw new Error('Network issue');
  }
};

export const downloadFile = (blob: any, fileName: string, fileExt: string) => {
  const a = document.createElement('a');
  document.body.appendChild(a);
  const url = window.URL.createObjectURL(blob);
  a.href = url;
  a.download = `${fileName}.${fileExt}`;
  a.click();
  setTimeout(() => {
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }, 0);
};

export const removeLineBreaks = (str: string) => {
  str = str.replace(/\r?\n|\r/g, ' '); // Remove line breaks
  str = str.replace(/\b([IVXLCDMivxlcdm]+)\.\s+/g, '$1.');
  return str;
};

export const highlightedSentence = (
  sentence: string,
  instance: any,
  setOpenErrorMsg?: any
): Promise<void> => {
  return new Promise<void>((resolve, reject) => {
    if (!instance) {
      reject('PDFTron instance not available');
      return;
    }

    const { Core } = instance;
    const { documentViewer, Annotations, annotationManager } = Core;
    const annotations = annotationManager.getAnnotationsList();
    const searchMode =
      instance.Core.Search.Mode.PAGE_STOP | instance.Core.Search.Mode.HIGHLIGHT;
    let textPresent = 0;
    const searchOptions = {
      fullSearch: true,
      onResult: (result: any) => {
        if (result.resultCode === instance.Core.Search.ResultCode.FOUND) {
          textPresent = 1;
          const highlight = new Annotations.TextHighlightAnnotation();
          result.quads.forEach((quad: any) => {
            highlight.Quads.push(quad.getPoints());
          });

          highlight.PageNumber = result.pageNum;
          annotationManager.addAnnotation(highlight);
          annotationManager.redrawAnnotation(highlight);
          annotationManager.drawAnnotations({ pageNumber: result.pageNum });

          if (documentViewer.getCurrentPage() !== result.pageNum) {
            documentViewer.setCurrentPage(result.pageNum, true);
          }
        }
      },
      onDocumentEnd: () => {
        if (textPresent === 0) {
          setOpenErrorMsg(true);
        }
        resolve(); // Resolve the promise once highlighting is completed
      },
    };
    documentViewer.textSearchInit(
      removeLineBreaks(sentence),
      searchMode,
      searchOptions
    );
    annotationManager.deleteAnnotations(annotations);
  });
};

export const getTodayDate = (type?: string) => {
  const timeElapsed = Date.now();
  const date = new Date(timeElapsed);

  const time = date.toLocaleString([], { hour: '2-digit', minute: '2-digit' });

  const fullTime = date.toLocaleString([], { hour12: true });

  const [month, day, year] = [
    date.getMonth(),
    date.getDate(),
    date.getFullYear(),
  ];

  switch (type) {
    case 'full-date-and-time':
      return `${day}/${month}/${year} ${fullTime}`;
    case 'full-date':
      return `${day}/${month}/${year}`;
    case 'full-time':
      return `${fullTime}`;
    case 'month':
      return month;
    case 'day':
      return day;
    case 'year':
      return year;
    default:
      return `${day}/${month}/${year} ${time}`;
  }
};

export const applyFields = async (instance: any) => {
  if (!instance) return;
  const { Annotations, documentViewer } = instance.Core;
  const annotationManager = documentViewer.getAnnotationManager();
  const fieldManager = annotationManager.getFieldManager();
  const annotationsList = annotationManager.getAnnotationsList();
  const doc = await documentViewer.getDocument().getPDFDoc();
  doc?.initSecurityHandler();
  doc?.lock();
  const annotsToDraw: any = [];

  await Promise.all(
    annotationsList.map(async (annot: any, index: number) => {
      let inputAnnot;
      let field;

      if (annot.getCustomData('type') === 'SIGNATURE') {
        const flags = new instance.Core.Annotations.WidgetFlags({});
        flags.set('Required', true);
        field = new Annotations.Forms.Field(
          `${annot.getContents()}-${Date.now()}-${index} ${annot.getCustomData(
            'value'
          )}`,
          {
            type: 'Sig',
            flags: flags,
          }
        );

        inputAnnot = new Annotations.SignatureWidgetAnnotation(field, {
          appearance: '_DEFAULT',
          appearances: {
            _DEFAULT: {
              Normal: {
                data: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAAJcEhZcwAADsMAAA7DAcdvqGQAAAAYdEVYdFNvZnR3YXJlAHBhaW50Lm5ldCA0LjEuMWMqnEsAAAANSURBVBhXY/j//z8DAAj8Av6IXwbgAAAAAElFTkSuQmCC',
                offset: {
                  x: 100,
                  y: 100,
                },
              },
            },
          },
        });

        // set position
        inputAnnot.PageNumber = annot.getPageNumber();
        inputAnnot.X = annot.getX();
        inputAnnot.Y = annot.getY();
        inputAnnot.rotation = annot.Rotation;
        if (annot.Rotation === 0 || annot.Rotation === 180) {
          inputAnnot.Width = annot.getWidth();
          inputAnnot.Height = annot.getHeight();
        } else {
          inputAnnot.Width = annot.getHeight();
          inputAnnot.Height = annot.getWidth();
        }
        inputAnnot.setCustomData('user', annot.getCustomData('value'));
      } else {
        return;
      }

      annot.TextColor = new Annotations.Color(0, 0, 0);
      annot.FillColor = new Annotations.Color(255, 255, 255, 0);
      annot.FontSize = '16px';
      annot.StrokeThickness = 0;
      annot.TextAlign = 'left';
      annot.Y = annot.getY() + annot.getHeight() - 2;
      annot.setCustomData('user', annot.getCustomData('value'));

      // draw the annotation the viewer
      annotationManager.addAnnotation(inputAnnot);
      fieldManager.addField(field);
      annotsToDraw.push(inputAnnot);
    })
  );
  // refresh viewer
  await annotationManager.drawAnnotationsFromList(annotsToDraw);
};

export const removeAnnotation = async (
  instance: any,
  tempSignatoryId?: any
) => {
  if (!instance) return;
  const { documentViewer, Annotations } = instance.Core;
  const annotationManager = documentViewer.getAnnotationManager();
  const annots = annotationManager.getAnnotationsList();
  const annotsToDraw: any = [];

  annots.forEach((annot: any) => {
    if (
      (annot instanceof Annotations.SignatureWidgetAnnotation &&
        annot.getCustomData('user') === tempSignatoryId) ||
      tempSignatoryId === annot.getCustomData('value')
    ) {
      annotationManager.deleteAnnotation(annot);
    }
  });

  // delete old annotations
  // refresh viewer
  await annotationManager.drawAnnotationsFromList(annotsToDraw);
};

export const handleSeeMoreAndLess = (text: string, length = 20) => {
  const words = text?.split(/\s+/);
  if (words?.length > length) {
    const sentence = words?.slice(0, length)?.join(' ');
    return `${sentence}...`;
  } else {
    return words?.join(' ');
  }
};

export const formatPayloadDate = (date: Date | null): string => {
  if (!date) return '';
  const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return localDate.toISOString().split('T')[0];
};

export const downloadPDFFile = (data: any) => {
  const filename = data?.file_name;
  // Remove the file extension
  const title = filename.replace(/\.[^/.]+$/, '');

  fetch(data?.location)
    .then((res) => res.blob())
    .then((blobData) => {
      saveAs(blobData, `${title}.pdf`);
    });
};

export const download_file = (data: any, file_name?: string) => {
  const filename = data?.file_name;

  fetch(data?.location)
    .then((res) => res.blob())
    .then((blobData) => {
      saveAs(blobData, filename ? filename : file_name);
    });
};

export const downloadDocxFile = (data: any) => {
  const filename = data?.file_name;
  // Remove the file extension
  const title = filename.replace(/\.[^/.]+$/, '');

  fetch(data?.location)
    .then((res) => res.blob())
    .then((blobData) => {
      saveAs(blobData, `${title}.docx`);
    });
};

export const sortListAlphabetical = (list: any, labelKey?: string) => {
  if (Array.isArray(list)) {
    const sortAlphabetical = list?.sort((a: any, b: any) => {
      return a?.[labelKey || 'name']?.localeCompare(b?.[labelKey || 'name']);
    });
    return sortAlphabetical || [];
  }
  return [];
};

export const droppedMsg =
  'This action can’t be performed, the draft has been dropped by the owner!';

export const automaticallyDroppedMsg =
  'This action can’t be performed, the connected document has been terminated!';

export const signatureMethod = [
  'imageSignaturePanelButton',
  'inkSignaturePanelButton',
  'textSignaturePanelButton',
];

export function getTimezoneAbbreviation(date: any) {
  // Convert the date into human readable
  // An example: Fri Jul 09 2021 13:07:25 GMT+0200 (Central European Summer Time)
  // As you can see there is the timezone

  date = date.toString();

  // We get the timezone matching everything the is inside the brackets/parentheses
  let timezone = date.match(/\(.+\)/g)[0];

  // We remove the brackets/parentheses
  timezone = timezone.replace('(', '').replace(')', '');

  // A new variable for the abbreviation
  let abbreviation = '';

  // We run a forEach dividing the timezone in words with split
  timezone.split(' ').forEach((word: string) => {
    // We insert the first letter of every word in the abbreviation variable
    abbreviation += word.split('')[0];
  });

  // And finally we return the abbreviation
  return abbreviation;
}

export const draftTabStatus = [
  'internal draft created',
  'deviation approval pending',
  'pre signatories approval pending',
  'counter party draft uploaded',
];

export const isStatusMatched = (status: any) => {
  return draftTabStatus?.some((item) => status?.toLowerCase().includes(item));
};

// Prevent duplicate comments submission
export const handleEnterKeySubmit = (
  event: React.KeyboardEvent<HTMLInputElement>,
  id: string,
  isSubmittingRef: React.MutableRefObject<{ [key: string]: boolean }>,
  onSubmit: (data: any, id: string) => void
) => {
  if (event.key === 'Enter' && !event.shiftKey) {
    event.preventDefault();

    if (isSubmittingRef.current[id]) return;
    isSubmittingRef.current[id] = true;

    const inputElement = event.target as HTMLInputElement;
    const commentText = inputElement?.value?.trim();

    if (commentText) {
      const commentData = { [`comment-${id}`]: commentText };
      onSubmit(commentData, id);
      inputElement.value = ''; // Clears input field after submission
    }
    // Enable input field after delay
    setTimeout(() => {
      isSubmittingRef.current[id] = false;
    }, 500);
  }
};
